import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesFeed, VoicesFeedEntry } from "../../../Models/voicesCampaigns";
import { useVoicesFeedCheck } from "../../../Query/useVoicesCampaigns";

interface Props {
  campaignId: number;
  feed: VoicesFeed | null;
  hasSearchTerms: boolean;
  onClose: () => void;
}

const VoicesFeedCheckDialog: React.FC<Props> = ({
  campaignId,
  feed,
  hasSearchTerms,
  onClose,
}) => {
  const { t } = useTranslation("voicesCampaigns");
  const { colors: workspace } = useWorkspaceColors();
  const { data: check, isFetching, isError } = useVoicesFeedCheck(
    campaignId,
    feed ? feed.id : null,
  );

  const renderEntry = (entry: VoicesFeedEntry, index: number) => {
    const source = entry.sources[0];
    return (
      <Box
        key={index}
        sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${color.gray200}` }}
      >
        <Link
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ color: color.gray900, fontWeight: 600 }}
        >
          {entry.title}
        </Link>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {source.date && new Date(`${source.date}T00:00:00`).toLocaleDateString()}
          {source.date && entry.summary && " · "}
          {entry.summary}
        </Typography>
      </Box>
    );
  };

  const renderResult = () => {
    if (isFetching) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
          <CircularProgress size={20} sx={{ color: workspace.accent }} />
          <Typography variant="body2">{t("feedChecking")}</Typography>
        </Box>
      );
    }
    if (isError || !check) {
      return null;
    }
    if (!check.readable) {
      return (
        <Typography variant="body2" sx={{ color: color.red700 }}>
          {t("feedUnreadable")}
        </Typography>
      );
    }
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2">
          {hasSearchTerms
            ? t("feedMatches", { count: check.entries.length })
            : t("feedEntries", { count: check.entries.length })}
        </Typography>
        {check.entries.length > 0 && (
          <Stack
            sx={{
              border: `1px solid ${color.gray200}`,
              borderBottom: "none",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {check.entries.map(renderEntry)}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={!!feed} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600, fontSize: "1.1rem" }}>
        {t("feedCheckTitle", { name: feed?.name })}
      </DialogTitle>
      <DialogContent dividers>{renderResult()}</DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("closeDialog")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesFeedCheckDialog;
